import { ArchiveOutlined, Delete } from "@mui/icons-material";
import { Grid, IconButton, LinearProgress, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import GoBack from "../../../../components/buttons/GoBack";
import PageContentWrapper from "../../../../components/wrappers/PageContentWrapper";
import BoxProvider from "../../../../contexts/BoxProvider";
import { ARCHIVE_BOX, GET_SINGLE_BOX } from "../../../../enums/ApiPaths";
import {
  BOX_ARCHIVE_KEY,
  BOXES_LIST_KEY,
  SINGLE_BOX_KEY,
} from "../../../../enums/QueryKeys";
import { SAPS_HOME_PAGE } from "../../../../enums/UrlPaths";
import { useFetch } from "../../../../hooks/Fetching";
import useCheckPath from "../../../../hooks/useCheckPath";
import useConfirm from "../../../../hooks/useConfirm";
import useFormDelete from "../../../../hooks/useFormDelete";
import { saveBoxNavigationId, useIsMobile } from "../../../../utils/Functions";
import useSubmit from "../../../master/species/hooks/useSubmit";
import AlertBar from "../../components/display/AlertBar";
import NotFound, { ErrorMessage } from "../../components/display/NotFound";
import HorizontalStack from "../../components/wrappers/HorizontalStack";
import AlertList from "./components/boxView/AlertList";
import InpatientStay from "./components/boxView/InpatientStay";
import MedicalRecords from "./components/boxView/MedicalRecords";
import PetList from "./components/boxView/PetList";
import Therapies from "./components/boxView/Therapies";

export const single_box_view_title_size = "h6";

const SingleBoxView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const url = GET_SINGLE_BOX;
  const { data, isLoading, error, isInitialLoading } = useFetch(
    url,
    id,
    SINGLE_BOX_KEY + id,
  );
  saveBoxNavigationId(id);

  if (isLoading || isInitialLoading) {
    return (
      <PageContentWrapper>
        <LinearProgress />
      </PageContentWrapper>
    );
  } else if (error) {
    return (
      <ErrorMessage
        title={t("SomethingWentWrong")}
        subtitle={t("FailedToFetchBox")}
      />
    );
  } else if (!data && !isLoading && !isInitialLoading && !error) {
    return (
      <PageContentWrapper>
        <NotFound title={t("BoxNotFound", { id })} />
      </PageContentWrapper>
    );
  }

  return <BoxViewComponent data={data} />;
};

/**
 * Box component, actual components of the box view
 */
export function BoxViewComponent({ data, goBack = SAPS_HOME_PAGE }) {
  const { id } = useParams();
  const { t } = useTranslation();
  const numberOfPets = data?.pets?.length;
  const isMobile = useIsMobile();
  const isArchived = useCheckPath();

  return (
    <BoxProvider key={"single_box_contextg_provider_" + id}>
      <PageContentWrapper noSideMargin sideMargin={isMobile ? 1 : 3}>
        <Grid
          container
          gap={isMobile ? 2 : 3}
          direction="column"
          sx={{
            m: 0,
            width: "100%",
            flexWrap: "nowrap",
            overflowX: "hidden",
          }}
        >
          <GridItem>
            <HorizontalStack>
              <GoBack url={goBack} />
              {isArchived ? null : (
                <Stack
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  direction={"row"}
                >
                  {!numberOfPets ? null : <ArchiveBox />}
                  <DeleteBox boxId={data?.id} />
                </Stack>
              )}
            </HorizontalStack>
          </GridItem>
          {isArchived && (
            <GridItem>
              <AlertBar
                severity="warning"
                message={t("ArchiveDataUserInfo")}
              />
            </GridItem>
          )}
          <GridItem>
            <PetList pets={data?.pets} boxName={data?.name} />
          </GridItem>
          <GridItem>
            <Therapies numberOfPets={numberOfPets} />
          </GridItem>
          {isArchived ? null : (
            <GridItem>
              <AlertList />
            </GridItem>
          )}
          <GridItem>
            <InpatientStay pets={data?.pets || []} />
          </GridItem>
          <GridItem>
            <MedicalRecords pets={data?.pets || []} />
          </GridItem>
        </Grid>
      </PageContentWrapper>
    </BoxProvider>
  );
}

export default SingleBoxView;

function ArchiveBox() {
  const { id: boxId } = useParams();
  const path = ARCHIVE_BOX + boxId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const submission = useSubmit(null, path, BOX_ARCHIVE_KEY + boxId);
  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    t("AreYouSure"),
    t("Accept"),
    t("Cancel"),
    "warning",
  );

  async function handleArchive() {
    getMessage(t("ArchingBoxConfirmationMessage"));
    const ans = await confirm();
    if (ans) {
      const res = await submission.submitCallback();
      if (res) {
        queryClient.invalidateQueries([SINGLE_BOX_KEY + boxId]);
        queryClient.invalidateQueries([BOXES_LIST_KEY]);
      }
    }
  }

  return (
    <Fragment>
      <IconButton onClick={() => handleArchive()} title={t("Archive")}>
        <ArchiveOutlined />
      </IconButton>
      <ConfirmationDialog />
    </Fragment>
  );
}


function DeleteBox({ boxId = null }) {
  const { t } = useTranslation();
  const path = `/saps/box/delete/`;
  const id = boxId;
  const queryClient = useQueryClient();
  const confirmationMessage = t("DeleteBoxConfirmation");
  const refetch = null;
  const goBack = true;
  const navTo = SAPS_HOME_PAGE;

  const deletion = useFormDelete(
    path,
    id,
    confirmationMessage,
    refetch,
    goBack,
    navTo,
  );

  async function handleDelete() {
    const res = await deletion.submitForm();
    if (res) {
      queryClient.invalidateQueries([BOXES_LIST_KEY]);
    }
  }

  return (
    <>
      <IconButton
        color="error"
        onClick={() => handleDelete()}
        title={t("DeleteBox")}
      >
        <Delete />
      </IconButton>
      <deletion.ConfirmationDialog />
    </>
  );
}

function GridItem({ children }) {
  return (
    <Grid
      item
      xs={12}
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {children}
    </Grid>
  );
}
